// Question definitions for the SCS "Analysis of Adverse Events" wizard.
//
// Mirrors scsOverallExtentOfExposureQuestions / scsDescriptionOfStudiesQuestions:
// `number` is the stable badge shown in the CSR-style index badge (page order,
// hardcoded — it does not renumber as conditional sections toggle), `text` is
// the fallback wording and `tooltip` holds the ticket INFORMATION/helper text
// (the endpoint returns wording only, no tooltip map).
//
// At runtime the wording is overridden by
// GET /clinical_summaries/safety_summary/questions. Every answer on this page
// is saved under its own qAE* attribute, so `textKeys` lists the attributes to
// read the served wording from: the endpoint carries these questions under the
// Clinical Laboratory Evaluations (qLab*) and Description of Studies names, and
// the comparison questions (Q4–Q6) are the Description of Studies questions
// repeated per grouping, where only the badge number changes.

export const SCS_AAE_QUESTIONS = {
  qAETypes: {
    number: 1,
    required: true,
    text: "Which AEs do you want to report?",
  },
  qAEAdditionalGroupingName: {
    number: 2,
    textKeys: ["qLabAdditionalGroupingName"],
    text: "Which additional groupings do you want to compare? Name the grouping and then select the studies in this grouping.",
  },
  qAEComparisonType: {
    number: 3,
    textKeys: ["qLabComparisonType", "qComparisonType"],
    text: "How is the grouping compared?",
  },
  // Q4 / Q5 below are the Description of Studies branch questions (its Q8 / Q9)
  // shown for the comparison type picked in Q3.
  qAENonComparativeGroupNames: {
    number: 4,
    textKeys: ["qNonComparativeGroupNames"],
    text: "What are the actual names used to describe the treatment/analysis groups in the column headers?",
  },
  qAETreatmentGroups: {
    number: 4,
    textKeys: ["qTreatmentGroupNames"],
    text: "What are the actual names used to describe the treatment/analysis groups in the column headers? Please define the role for each column name.",
  },
  qAEComparisonPairs: {
    number: 5,
    textKeys: ["qComparisonPairs"],
    text: "Which dose/treatment groups should be compared?",
  },
  qAEDoseGroups: {
    number: 4,
    textKeys: ["qDoseGroups"],
    text: "Please list the dose group column headers from lowest to highest, starting with Placebo if present.",
  },
};

// The AE categories offered by Q1. `key` is the attribute the tables picked for
// that category are saved under inside qAETypes, `label` is the on-screen
// wording (the Q1 multi-select option and the sub-question label), and `letter`
// is the badge suffix of its sub-question (1A … 1I).
//
// The keys are the payload values the backend expects (one per adverse-events
// transformer); only the wording is a front-end concern.
//
// TODO: the questions endpoint carries no wording for these yet, so the labels
// are hardcoded here — swap them for the served wording once it is available.
export const SCS_AE_TYPE_OPTIONS = [
  { key: "safety_summary", label: "Overall AEs", letter: "A" },
  { key: "severity", label: "AEs by Severity", letter: "B" },
  { key: "treatment_related", label: "TRAEs", letter: "C" },
  { key: "discontinuation", label: "Discontinuation AEs", letter: "D" },
  { key: "deaths", label: "Deaths", letter: "E" },
  { key: "serious", label: "SAEs", letter: "F" },
  { key: "special_interest", label: "AESI", letter: "G" },
  { key: "other_significant", label: "Other Significant", letter: "H" },
  { key: "frequency", label: "Frequency", letter: "I" },
];

// Comparison type (Q3) -> the additional_groupings section the grouping is
// saved into (the same split the Description of Studies comparisons use).
export const SCS_AAE_GROUPING_SECTION = {
  "Non-Comparative": "single_arm",
  Comparative: "multi_arm",
  "Dose-Finding": "dose_finding",
};
